// Copy for the floating offer bar (AiAutomationBar), pitched per page.
// `title` supports inline HTML — <span> renders in brand green.
//
// buttonLink: "#hash" (same page) | "/internal" (router) | "https://..." (new tab)

const CALENDLY = "https://calendly.com/bitsclan-it/30min";

const DEFAULT_CONTENT = {
  title: "Turn your idea into <span>revenue-ready</span> software.",
  text: "Book a free 30-minute call and leave with a clear plan, not a sales pitch.",
  buttonText: "Book My Free Call",
  buttonLink: CALENDLY,
};

// Exact path -> content
const BY_PATH = {
  "/": {
    title: "Ship faster with a team that has <span>already done it</span>.",
    text: "Book a free call and get a straight answer on scope, cost, and timeline.",
    buttonText: "Book My Free Call",
    buttonLink: CALENDLY,
  },

  "/ai-automation-agency": {
    title:
      "Increase profit margins by up to <span>10%</span> with AI workflow automation.",
    text: "Book a free audit we’ll map the one workflow worth automating first.",
    buttonText: "Automate My Business",
    buttonLink: "#book-consultation",
  },

  "/ai-agent-development-services": {
    title: "Put an <span>AI agent</span> on your busiest workflow.",
    text: "Book a free call and see exactly what it can handle from day one.",
    buttonText: "Build My AI Agent",
    buttonLink: CALENDLY,
  },

  "/ai-chatbot-development-services": {
    title: "Resolve <span>80% of support tickets</span> without hiring.",
    text: "Book a free call and watch a chatbot handle your real queries.",
    buttonText: "Get My AI Chatbot",
    buttonLink: CALENDLY,
  },

  "/ai-ml-development-services": {
    title: "Turn your data into <span>decisions that pay</span>.",
    text: "Book a free consult we’ll scope your highest-ROI ML use case.",
    buttonText: "Talk to an ML Expert",
    buttonLink: CALENDLY,
  },

  "/ai-consultation-services": {
    title: "Not sure <span>where AI fits</span>? Find out in 30 minutes.",
    text: "Book a free consult and leave with a punch list you can act on.",
    buttonText: "Get My AI Roadmap",
    buttonLink: CALENDLY,
  },

  "/generative-ai-development-services": {
    title: "Ship <span>generative AI</span> your customers actually use.",
    text: "Book a free call we’ll pressure-test your use case before you build.",
    buttonText: "Start Building",
    buttonLink: CALENDLY,
  },

  "/computer-vision-development-services": {
    title: "Let machines <span>see what your team misses</span>.",
    text: "Book a free call to scope your computer vision build.",
    buttonText: "Book My Free Call",
    buttonLink: CALENDLY,
  },

  "/custom-software-development-services": {
    title: "Custom software, shipped in <span>weeks</span> not quarters.",
    text: "Book a free scoping call and get a fixed plan with a real timeline.",
    buttonText: "Scope My Project",
    buttonLink: CALENDLY,
  },

  "/web-app-development-services": {
    title: "A web app your users <span>won’t want to leave</span>.",
    text: "Book a free call and get a build plan with an honest timeline.",
    buttonText: "Start My Web App",
    buttonLink: CALENDLY,
  },

  "/mobile-app-development-services": {
    title: "Launch on <span>iOS and Android</span> with one team.",
    text: "Book a free call and get your launch plan in 30 minutes.",
    buttonText: "Start My App",
    buttonLink: CALENDLY,
  },

  "/ecommerce-services": {
    title: "Recover the <span>70% of carts</span> you lose today.",
    text: "Book a free call we’ll find the leaks in your checkout funnel.",
    buttonText: "Grow My Store",
    buttonLink: CALENDLY,
  },

  "/dev-ops-services": {
    title: "Deploy <span>10× more often</span>, break things far less.",
    text: "Book a free DevOps health check on your pipeline.",
    buttonText: "Fix My Pipeline",
    buttonLink: CALENDLY,
  },

  // "/cyber-security-services" removed — that page is retired and the route
  // now 301s to the home page, which has its own entry above.

  "/data-analytics-services": {
    title: "Stop guessing. <span>Start knowing.</span>",
    text: "Book a free call and turn your data into dashboards that drive action.",
    buttonText: "Unlock My Data",
    buttonLink: CALENDLY,
  },

  "/quality-assurance-services": {
    title: "Ship with <span>zero surprises</span> in production.",
    text: "Book a free QA audit of your release process.",
    buttonText: "Improve My QA",
    buttonLink: CALENDLY,
  },

  "/portfolio": {
    title: "Like what you see? <span>Let’s build yours.</span>",
    text: "Book a free call and we’ll scope your project on the spot.",
    buttonText: "Start My Project",
    buttonLink: CALENDLY,
  },

  "/about-us": {
    title: "Let’s build something <span>worth talking about</span>.",
    text: "Book a free 30-minute call with the team that will do the work.",
    buttonText: "Book My Free Call",
    buttonLink: CALENDLY,
  },

  "/life-at-bitsclan": {
    title: "Great people build <span>great software</span>.",
    text: "Book a free call and meet the team behind the work.",
    buttonText: "Book My Free Call",
    buttonLink: CALENDLY,
  },

  "/engagment-model": {
    title: "Find the engagement model that <span>fits your budget</span>.",
    text: "Book a free call we’ll recommend the right one in 30 minutes.",
    buttonText: "Book My Free Call",
    buttonLink: CALENDLY,
  },

  "/Industries": {
    title: "Built for <span>your industry</span>, not from a template.",
    text: "Book a free call and see what we’ve shipped in your space.",
    buttonText: "Book My Free Call",
    buttonLink: CALENDLY,
  },
};

// Prefix rules, checked in order when no exact match is found.
const BY_PREFIX = [
  {
    prefix: "/hire-",
    content: {
      title: "Hire <span>vetted engineers</span> — start in 48 hours.",
      text: "Book a free call and meet pre-screened developers this week.",
      buttonText: "Hire Developers",
      buttonLink: CALENDLY,
    },
  },
  {
    prefix: "/case-studies",
    content: {
      title: "Want results like <span>these</span>?",
      text: "Book a free call and we’ll map the same playbook to your business.",
      buttonText: "Book My Free Call",
      buttonLink: CALENDLY,
    },
  },
  {
    // Inert while the blog lives in the Next.js app (this bar is rendered by
    // the React app only), but kept correct for if it ever moves back.
    prefix: "/blogs",
    content: {
      title: "Reading is good. <span>Shipping is better.</span>",
      text: "Book a free call and turn these ideas into a working system.",
      buttonText: "Book My Free Call",
      buttonLink: CALENDLY,
    },
  },
  {
    prefix: "/industries",
    content: {
      title: "Built for <span>your industry</span>, not from a template.",
      text: "Book a free call and see what we’ve shipped in your space.",
      buttonText: "Book My Free Call",
      buttonLink: CALENDLY,
    },
  },
  // Regional AI chatbot landing pages share the chatbot pitch.
  {
    prefix: "/california-ai-chatbot",
    content: BY_PATH["/ai-chatbot-development-services"],
  },
  {
    prefix: "/florida-ai-chatbot",
    content: BY_PATH["/ai-chatbot-development-services"],
  },
  {
    prefix: "/ai-chatbot-development-newyork",
    content: BY_PATH["/ai-chatbot-development-services"],
  },
  {
    prefix: "/ai-chatbot-development-company-texas",
    content: BY_PATH["/ai-chatbot-development-services"],
  },
];

/**
 * Resolve the floating-bar copy for a route.
 * @param {string} pathname
 * @returns {{title:string, text:string, buttonText:string, buttonLink:string}}
 */
export const getFloatingBarContent = (pathname = "/") => {
  if (BY_PATH[pathname]) return BY_PATH[pathname];

  const lower = pathname.toLowerCase();
  const match = BY_PREFIX.find((rule) => lower.startsWith(rule.prefix.toLowerCase()));
  return match ? match.content : DEFAULT_CONTENT;
};

export default getFloatingBarContent;
